import React, { useMemo } from "react";

import { Box, CardMedia, IconButton, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import clsx from "clsx";

import Price from "components/Price/Price";
import useCart from "hooks/useCart";
import useLocalize from "hooks/useLocalize";
import useTranslation from "hooks/useTranslation";
import EditCircle from "icons/EditCircle";
import TrashIcon from "icons/TrashIcon";

const useStyles = makeStyles(theme => {
	return {
		card: {
			backgroundColor: "transparent",
			display: "flex",
			width: "100%",
			color: "#fff",
			borderRadius: 0
		},
		cardAction: {
			width: "100%",
			padding: 0,
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(2),
			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between",
			position: "relative",
			marginRight: theme.spacing(1.5),
			borderBottom: "1px solid",
			borderBottomColor: theme.palette.background.paper,
			color: theme.palette.text.primary
		},
		item: {
			display: "flex",
			flexDirection: "row",
			alignContent: "center",
			alignItems: "center"
		},
		itemCode: {
			fontSize: 12,
			marginRight: theme.spacing(1),
			position: "absolute",
			left: "0"
		},
		itemName: {
			fontSize: 12,
			fontWeight: 500,
			textTransform: "uppercase",
			textOverflow: "ellipsis",
			color: "inherit",
			maxWidth: "200px",
			position: "relative",
			paddingLeft: theme.spacing(3)
		},
		optionRow: {
			paddingTop: theme.spacing(1),
			paddingLeft: theme.spacing(2)
		},
		option: {
			fontSize: 12,
			textTransform: "uppercase",
			color: "#a4a4a4"
		},
		instructions: {
			paddingTop: theme.spacing(1),
			paddingLeft: theme.spacing(2),
			fontSize: 12,
			textTransform: "uppercase",
			color: "#a4a4a4"
		},
		price: {
			fontSize: 12
		},
		itemRow: {
			width: "100%",
			display: "flex",
			fontWeight: 500,
			flexDirection: "row",
			alignContent: "center",
			alignItems: "center",
			justifyContent: "space-between"
		},
		totalRow: {
			display: "flex",
			flexDirection: "row",
			alignContent: "flex-end",
			justifyContent: "flex-end"
		},
		actionButtonsRow: {
			display: "flex",
			flexDirection: "row",
			alignContent: "flex-end",
			justifyContent: "flex-end",
			marginTop: theme.spacing(1)
		},
		total: {
			fontSize: 12,
			fontWeight: 500
		},
		remove: {
			textAlign: "left",
			padding: "0",
			marginLeft: "8px"
		},
		icon: {
			height: 16,
			fill: theme.palette.text.primary
		},
		edit: {
			textAlign: "left",
			padding: "0"
		},
		qty: {
			fontSize: 12,
			marginRight: theme.spacing(1)
		}
	};
});

interface CartItemProps {
	props?: any;
	cartItem: any;
}

const CartItemDesktop: React.FC<CartItemProps> = ({ cartItem }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { removeFromCart } = useCart();

	const { menuItem } = cartItem;

	const handleRemoveClick = useMemo(() => () => removeFromCart(cartItem), [cartItem, removeFromCart]);

	const handleEditClick = useMemo(() => () => {}, []);

	const selectedOptions: any[] = [];
	if (cartItem.selectedOptions.length > 0) {
		cartItem.selectedOptions.map(s => {
			const itemOption = menuItem.menuItemOptionGroups
				.find(g => g.id === s.menuItemOptionGroupId)
				?.menuItemOptions.find(o => o.id === s.menuItemOptionId);
			selectedOptions.push(itemOption);
		});
	}
	return (
		<Paper elevation={0} className={classes.card}>
			<Box className={classes.cardAction}>
				<Box className={classes.itemRow}>
					<Box className={classes.item}>
						<Typography className={classes.itemName}>
							<span className={classes.itemCode}>{menuItem.itemCode && menuItem.itemCode}</span>
							{localize(menuItem.localeNames, menuItem.name)}
						</Typography>
					</Box>
					<Box className={classes.totalRow}>
						{cartItem.quantity > 1 ? (
							<Typography className={classes.qty}>{`${t("qty")} ${cartItem.quantity}`}&nbsp;&nbsp;-</Typography>
						) : null}
						<Price className={classes.total} priceCents={cartItem.netPrice || cartItem.extendedPriceCents} />
					</Box>
				</Box>
				{cartItem.specialInstructions && <Box className={classes.instructions}>{cartItem.specialInstructions}</Box>}
				{selectedOptions.length > 0 &&
					selectedOptions.map(o => (
						<Box key={o.id} className={clsx(classes.item, classes.optionRow)}>
							{o.itemCode && <Typography className={clsx(classes.itemCode, classes.option)}>{o.itemCode}</Typography>}
							<Typography className={clsx(classes.itemName, classes.option)}>
								{localize(o.localeNames, o.name)}
							</Typography>
						</Box>
					))}

				{cartItem.instructions && <Box className={classes.instructions}>{cartItem.instructions}</Box>}
				<Box className={classes.actionButtonsRow}>
					<IconButton
						component="a"
						href={`#cart:${cartItem.localId}`}
						className={classes.edit}
						onClick={handleEditClick}
						title={t("edit")}
					>
						<EditCircle className={classes.icon} />
					</IconButton>
					<IconButton component="a" className={classes.remove} onClick={handleRemoveClick} title={t("remove")}>
						<TrashIcon className={classes.icon} />
					</IconButton>
				</Box>
			</Box>
		</Paper>
	);
};

export default CartItemDesktop;
